import React, { useEffect, useMemo, useState } from "react";

const REPORT_JSON_BASE =
  "https://educationforall.app.n8n.cloud/webhook/report-client-analytics";

const DEFAULT_RANGE = "last_30_days";

type RangeKey = "today" | "last_7_days" | "last_30_days" | "all_time";

const RANGE_OPTIONS: Array<{
  key: RangeKey;
  label: string;
  displayLabel: string;
}> = [
    { key: "today", label: "Today", displayLabel: "Today" },
    { key: "last_7_days", label: "7D", displayLabel: "Past 7 days" },
    { key: "last_30_days", label: "30D", displayLabel: "Last 30 days" },
    { key: "all_time", label: "All", displayLabel: "All" },
  ];

type OverviewTotals = {
  clientCount?: number;
  totalUserMessages?: number;
  realtimeVoiceCount?: number;
  copingBuddyEvents?: number;
  learningBuddyEvents?: number;
  reassuranceDetections?: number;
  safetyEvents?: number;
};

type OverviewClient = {
  accessCode?: string;
  totalUserMessages?: number;
  typedCount?: number;
  dictationCount?: number;
  realtimeVoiceCount?: number;
  totalAssistantMessages?: number;
  assistantTextCount?: number;
  assistantVoicePlaybackCount?: number;
  copingBuddyEvents?: number;
  learningBuddyEvents?: number;
  activityStartedCount?: number;
  activityCompletedCount?: number;
  reassuranceDetections?: number;
  safetyEvents?: number;
  lastEventAt?: string;
};

type FilteredTotals = {
  clientCount: number;
  totalUserMessages: number;
  realtimeVoiceCount: number;
  copingBuddyEvents: number;
  learningBuddyEvents: number;
  reassuranceDetections: number;
  safetyEvents: number;
};

type OverviewPayload = {
  reportType?: string;
  range?: string;
  start?: string;
  end?: string;
  totals?: OverviewTotals;
  clients?: OverviewClient[];
  notes?: string[];
};

function normalizeRange(v: string | null): RangeKey {
  const range = (v ?? "").toLowerCase().trim();

  if (range === "today") return "today";
  if (range === "last_7_days") return "last_7_days";
  if (range === "last_30_days") return "last_30_days";
  if (range === "all" || range === "all_time") return "all_time";

  return "last_30_days";
}

function rangeLabel(range: RangeKey): string {
  return (
    RANGE_OPTIONS.find((option) => option.key === range)?.displayLabel ??
    "Last 30 days"
  );
}

function n(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) {
    return Number(v);
  }
  return 0;
}

function buildReportUrl(range: RangeKey): string {
  const u = new URL(REPORT_JSON_BASE);
  u.searchParams.set("scope", "overview");
  u.searchParams.set("range", range);
  return u.toString();
}

function csvEscape(value: unknown): string {
  const s = String(value ?? "");
  const needsQuotes = /[",\n\r]/.test(s);

  const escaped = s.replace(/"/g, '""');

  return needsQuotes ? `"${escaped}"` : escaped;
}

function MetricCard({
  label,
  value,
}: {
  label: string;
  value: number | string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-sm font-medium text-slate-900">{label}</div>
      <div className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">
        {value}
      </div>
    </div>
  );
}

type SegmentOption<K extends string> = {
  key: K;
  label: string;
};

function SegmentedControl<K extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: Array<SegmentOption<K>>;
  value: K;
  onChange: (key: K) => void;
  ariaLabel: string;
}) {
  return (
    <div
      className="inline-flex w-full rounded-full bg-slate-100 p-1 md:w-auto"
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const isActive = value === option.key;

        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(option.key)}
            className={[
              "inline-flex flex-1 items-center justify-center rounded-full px-4 py-2.5 text-sm font-bold uppercase tracking-wide transition md:flex-none",
              isActive
                ? "bg-slate-950 text-white shadow-sm"
                : "text-slate-600 hover:bg-white/60 hover:text-slate-950",
            ].join(" ")}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default function AnalyticsOverviewReportPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const [range, setRangeState] = useState<RangeKey>(() =>
    normalizeRange(params.get("range"))
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<OverviewPayload | null>(null);
  const [selectedAccessCodes, setSelectedAccessCodes] = useState<Set<string>>(
    () => new Set()
  );

  function setRange(nextRange: RangeKey) {
    setRangeState(nextRange);

    const next = new URLSearchParams(window.location.search);
    next.set("range", nextRange);

    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${next.toString()}`
    );
  }

  function saveOverviewPdf() {
    const previousTitle = document.title;

    const safeRange = range.replace(/[^a-z0-9_-]/gi, "_");
    const exportedAt = new Date().toISOString().slice(0, 10);

    document.title = `analytics-overview-${safeRange}-${exportedAt}`;

    window.print();

    window.setTimeout(() => {
      document.title = previousTitle;
    }, 500);
  }

  useEffect(() => {
    let cancelled = false;
    const url = buildReportUrl(range);

    (async () => {
      setLoading(true);
      setError(null);
      setPayload(null);

      try {
        const res = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
        });

        const text = await res.text();

        let data: unknown = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          throw new Error("Response was not valid JSON");
        }

        if (cancelled) return;

        if (!res.ok) {
          const msg =
            typeof data === "object" &&
              data !== null &&
              "message" in data &&
              typeof (data as { message?: unknown }).message === "string"
              ? String((data as { message?: unknown }).message)
              : undefined;

          throw new Error(msg || `Request failed (${res.status})`);
        }

        const root =
          Array.isArray(data) && data.length > 0 && typeof data[0] === "object"
            ? (data[0] as OverviewPayload)
            : (data as OverviewPayload);

        setPayload(root);

        const accessCodes = (root.clients ?? [])
          .map((client) => client.accessCode)
          .filter((code): code is string => Boolean(code));

        setSelectedAccessCodes(new Set(accessCodes));
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load report");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [range]);

  const clients = payload?.clients ?? [];
  const totals = payload?.totals ?? {};
  const notes = payload?.notes ?? [];

  const allAccessCodes = clients
    .map((client) => client.accessCode)
    .filter((code): code is string => Boolean(code));

  const visibleClients = clients.filter((client) => {
    const code = client.accessCode;
    if (!code) return false;
    return selectedAccessCodes.has(code);
  });

  const filteredTotals = visibleClients.reduce<FilteredTotals>(
    (acc, client) => {
      acc.clientCount += 1;
      acc.totalUserMessages += n(client.totalUserMessages);
      acc.realtimeVoiceCount += n(client.realtimeVoiceCount);
      acc.copingBuddyEvents += n(client.copingBuddyEvents);
      acc.learningBuddyEvents += n(client.learningBuddyEvents);
      acc.reassuranceDetections += n(client.reassuranceDetections);
      acc.safetyEvents += n(client.safetyEvents);
      return acc;
    },
    {
      clientCount: 0,
      totalUserMessages: 0,
      realtimeVoiceCount: 0,
      copingBuddyEvents: 0,
      learningBuddyEvents: 0,
      reassuranceDetections: 0,
      safetyEvents: 0,
    }
  );

  function toggleAccessCode(accessCode: string) {
    setSelectedAccessCodes((prev) => {
      const next = new Set(prev);

      if (next.has(accessCode)) {
        next.delete(accessCode);
      } else {
        next.add(accessCode);
      }

      return next;
    });
  }

  function selectAllAccessCodes() {
    setSelectedAccessCodes(new Set(allAccessCodes));
  }

  function clearAccessCodes() {
    setSelectedAccessCodes(new Set());
  }

  function exportClientActivityCsv() {
    const headers = [
      "Access code",
      "User messages",
      "Typed",
      "Dictation",
      "Realtime voice",
      "Coping Buddy events",
      "Learning Buddy events",
      "Reassurance detections",
      "Safety events",
      "Last event",
    ];

    const rows = visibleClients.map((client) => [
      client.accessCode ?? "",
      n(client.totalUserMessages),
      n(client.typedCount),
      n(client.dictationCount),
      n(client.realtimeVoiceCount),
      n(client.copingBuddyEvents),
      n(client.learningBuddyEvents),
      n(client.reassuranceDetections),
      n(client.safetyEvents),
      client.lastEventAt ?? "",
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");

    const blob = new Blob([`\uFEFF${csv}`], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    const safeRange = range.replace(/[^a-z0-9_-]/gi, "_");
    const exportedAt = new Date().toISOString().slice(0, 10);

    a.href = url;
    a.download = `client-activity-${safeRange}-${exportedAt}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  }

  return (
    <div className="h-screen overflow-y-auto bg-slate-50 text-slate-900 print:h-auto print:overflow-visible">
      <div className="mx-auto max-w-6xl px-4 py-10 print:max-w-none print:px-0 print:py-0">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Analytics Overview Report
        </h1>

        <p className="mt-2 text-sm text-slate-600">
          Overview report across access codes with client-side filters.
        </p>

        {loading && (
          <div className="mt-8 flex items-center gap-2 text-sm text-slate-600">
            <span
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"
              aria-hidden
            />
            Loading overview…
          </div>
        )}

        {error && (
          <div
            className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
            role="alert"
          >
            {error}
          </div>
        )}

        {!loading && !error && payload && (
          <div className="mt-8 space-y-8 pb-12">
            <dl className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium uppercase text-slate-500">
                  Report type
                </dt>
                <dd className="mt-0.5 font-medium text-slate-900">
                  {payload.reportType ?? "overview"}
                </dd>
              </div>

              <div>
                <dt className="text-xs font-medium uppercase text-slate-500">
                  Range
                </dt>
                <dd className="mt-0.5 font-medium text-slate-900">
                  {rangeLabel(range)}
                </dd>
              </div>

              {payload.start && (
                <div>
                  <dt className="text-xs font-medium uppercase text-slate-500">
                    Start
                  </dt>
                  <dd className="mt-0.5 text-slate-800">{payload.start}</dd>
                </div>
              )}

              {payload.end && (
                <div>
                  <dt className="text-xs font-medium uppercase text-slate-500">
                    End
                  </dt>
                  <dd className="mt-0.5 text-slate-800">{payload.end}</dd>
                </div>
              )}

              <div className="sm:col-span-2">
                <dt className="text-xs font-medium uppercase text-slate-500">
                  Access codes returned
                </dt>
                <dd className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">
                  {n(totals.clientCount)}
                </dd>
              </div>
            </dl>

            <div className="print:hidden">
              <div className="mb-2 text-xs font-medium uppercase text-slate-500">
                Date range
              </div>

              <div className="flex flex-col gap-3 rounded-[28px] border border-slate-200/80 bg-white/80 p-2 shadow-sm backdrop-blur sm:flex-row sm:items-center sm:justify-between">
                <SegmentedControl<RangeKey>
                  options={RANGE_OPTIONS}
                  value={range}
                  onChange={setRange}
                  ariaLabel="Date range"
                />

                <button
                  type="button"
                  onClick={saveOverviewPdf}
                  disabled={!payload}
                  title="Save the overview report as a PDF"
                  className="rounded-full bg-white px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Save PDF
                </button>
              </div>
            </div>

            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-medium uppercase text-slate-500">
                    Access code filter
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    Showing {visibleClients.length} of {clients.length} access
                    codes.
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllAccessCodes}
                    className="rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                  >
                    Select all
                  </button>

                  <button
                    type="button"
                    onClick={clearAccessCodes}
                    className="rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="max-h-36 overflow-y-auto rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                {allAccessCodes.length > 0 ? (
                  <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                    {allAccessCodes.map((code) => (
                      <label
                        key={code}
                        className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          checked={selectedAccessCodes.has(code)}
                          onChange={() => toggleAccessCode(code)}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                        <span className="font-medium text-slate-800">
                          {code}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-slate-600">
                    No access codes found for this date range.
                  </div>
                )}
              </div>
            </div>

            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Filtered totals
              </h2>

              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <MetricCard label="Clients" value={filteredTotals.clientCount} />
                <MetricCard
                  label="User messages"
                  value={filteredTotals.totalUserMessages}
                />
                <MetricCard
                  label="Realtime voice"
                  value={filteredTotals.realtimeVoiceCount}
                />
                <MetricCard
                  label="Coping Buddy events"
                  value={filteredTotals.copingBuddyEvents}
                />
                <MetricCard
                  label="Learning Buddy events"
                  value={filteredTotals.learningBuddyEvents}
                />
                <MetricCard
                  label="Reassurance detections"
                  value={filteredTotals.reassuranceDetections}
                />
                <MetricCard
                  label="Safety events"
                  value={filteredTotals.safetyEvents}
                />
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Client activity table
                </h2>

                <button
                  type="button"
                  onClick={exportClientActivityCsv}
                  disabled={visibleClients.length === 0}
                  title="Export the currently visible client activity table"
                  className="rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Export CSV
                </button>
              </div>

              <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="max-h-[560px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="sticky top-0 z-10 bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600">
                          Access code
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600">
                          User msgs
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600">
                          Typed
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600">
                          Dictation
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600">
                          Realtime voice
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600">
                          Coping
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600">
                          Learning
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600">
                          Reassurance
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600">
                          Safety
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600">
                          Last event
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100 bg-white">
                      {visibleClients.map((client) => (
                        <tr key={client.accessCode}>
                          <td className="px-4 py-3 font-medium text-slate-900">
                            {client.accessCode}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            {n(client.totalUserMessages)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            {n(client.typedCount)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            {n(client.dictationCount)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            {n(client.realtimeVoiceCount)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            {n(client.copingBuddyEvents)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            {n(client.learningBuddyEvents)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            {n(client.reassuranceDetections)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                            {n(client.safetyEvents)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                            {client.lastEventAt ?? ""}
                          </td>
                        </tr>
                      ))}

                      {visibleClients.length === 0 && (
                        <tr>
                          <td
                            colSpan={10}
                            className="px-4 py-6 text-center text-slate-600"
                          >
                            No selected access codes.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {notes.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-sm">
                <span className="font-medium text-slate-900">Notes: </span>
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  {notes.map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}